'use client';

/* eslint-disable max-lines-per-function */
import React from 'react';
import { Link } from 'react-router-dom';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Sparkles, CheckCircle2, ArrowRight, Clock, ExternalLink } from 'lucide-react';
import { WhatsNewFeature } from '@/hooks/useWhatsNew';

// Shared class strings. Extracted because prettier cannot wrap a string
// literal, so inline Tailwind lists this long sit permanently over max-len.
const SECTION_HEADING_CLASS = 'text-xs font-semibold text-muted-foreground uppercase tracking-wide';
const REGULAR_ITEM_CLASS =
  'flex items-start gap-2 p-2 rounded-md border hover:bg-accent/50 transition-colors';
const HISTORY_ITEM_CLASS = 'p-2 rounded-md border bg-muted/30 hover:bg-muted/50 transition-colors';
const ARROW_ICON_CLASS = 'h-3.5 w-3.5 text-muted-foreground flex-shrink-0 mt-0.5';
const FOOTER_LINK_CLASS =
  'flex items-center justify-center gap-2 text-xs text-muted-foreground ' +
  'hover:text-primary transition-colors';

interface ReleaseHistory {
  releaseId: string;
  features: WhatsNewFeature[];
}

interface WhatsNewPopoverProps {
  trigger: React.ReactNode;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  releaseId: string;
  features: WhatsNewFeature[];
  releaseHistory?: ReleaseHistory[];
  onDismiss: () => void;
}

export const WhatsNewPopover: React.FC<WhatsNewPopoverProps> = ({
  trigger,
  open,
  onOpenChange,
  releaseId,
  features,
  releaseHistory = [],
  onDismiss
}) => {
  const handleGotIt = () => {
    onDismiss();
  };

  const highlightedFeatures = features.filter(f => f.highlight);
  const regularFeatures = features.filter(f => !f.highlight);

  // Format release ID for display (v2_0_oct_2025 -> v2.0 - Oct 2025)
  const formatReleaseId = (id: string): string => {
    const match = id.match(/^v(\d+)_(\d+)_(\w+)_(\d+)$/);
    if (match) {
      const [, major, minor, month, year] = match;
      const monthName = month ? month.charAt(0).toUpperCase() + month.slice(1) : '';
      return `v${major}.${minor} - ${monthName} ${year}`;
    }
    // Handle auto-generated release IDs (auto_2026_01_28 -> Jan 28, 2026)
    const autoMatch = id.match(/^auto_(\d{4})_(\d{2})_(\d{2})$/);
    if (autoMatch) {
      const [, year, month, day] = autoMatch;
      const date = new Date(Number(year), Number(month) - 1, Number(day));
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    }
    return id;
  };

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent className='w-80 sm:w-96 max-h-[80vh] overflow-y-auto' align='start'>
        <div className='space-y-4'>
          {/* Header */}
          <div className='flex items-center gap-3 pb-2 border-b'>
            <div className='rounded-full bg-primary/10 p-2'>
              <Sparkles className='h-5 w-5 text-primary' />
            </div>
            <div className='flex-1'>
              <h3 className='font-semibold text-lg'>What&apos;s New</h3>
              <p className='text-xs text-muted-foreground'>{formatReleaseId(releaseId)}</p>
            </div>
          </div>

          {/* Features List */}
          <div className='space-y-3'>
            {/* Highlighted Features */}
            {highlightedFeatures.length > 0 && (
              <div className='space-y-2'>
                {highlightedFeatures.map(feature => (
                  <div
                    key={feature.id}
                    className='rounded-lg border-2 border-primary/20 bg-primary/5 p-3 space-y-1.5'
                  >
                    <div className='flex items-start justify-between gap-2'>
                      <div className='flex-1 min-w-0'>
                        <h4 className='font-semibold text-sm mb-1'>{feature.title}</h4>
                        {feature.description && (
                          <p className='text-xs text-muted-foreground'>{feature.description}</p>
                        )}
                      </div>
                      <CheckCircle2 className='h-4 w-4 text-primary flex-shrink-0 mt-0.5' />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Regular Features */}
            {regularFeatures.length > 0 && (
              <div className='space-y-2'>
                {highlightedFeatures.length > 0 && (
                  <h5 className={SECTION_HEADING_CLASS}>Other Updates</h5>
                )}
                {regularFeatures.map(feature => (
                  <div key={feature.id} className={REGULAR_ITEM_CLASS}>
                    <ArrowRight className={ARROW_ICON_CLASS} />
                    <div className='flex-1 min-w-0'>
                      <h4 className='font-medium text-sm mb-0.5'>{feature.title}</h4>
                      {feature.description && (
                        <p className='text-xs text-muted-foreground'>{feature.description}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Release History - Last 5 releases */}
          {releaseHistory.length > 1 && (
            <div className='pt-3 border-t'>
              <div className='flex items-center gap-2 mb-2'>
                <Clock className='h-3.5 w-3.5 text-muted-foreground' />
                <h5 className={SECTION_HEADING_CLASS}>Recent Updates</h5>
              </div>
              <div className='space-y-1.5'>
                {releaseHistory
                  .filter(r => r.releaseId !== releaseId)
                  .slice(0, 4)
                  .map(release => {
                    // Bind the first feature rather than indexing inline: under
                    // noUncheckedIndexedAccess a `.length > 0` check does not
                    // narrow `features[0]`, so the element access reads as
                    // possibly undefined.
                    const [latestFeature] = release.features;
                    const otherCount = release.features.length - 1;
                    return (
                      <div key={release.releaseId} className={HISTORY_ITEM_CLASS}>
                        <div className='flex items-center justify-between mb-1'>
                          <span className='text-xs font-medium'>
                            {formatReleaseId(release.releaseId)}
                          </span>
                          <Badge variant='outline' className='text-xs h-4 px-1.5'>
                            {release.features.length} update
                            {release.features.length !== 1 ? 's' : ''}
                          </Badge>
                        </div>
                        {latestFeature && (
                          <p className='text-xs text-muted-foreground'>
                            {latestFeature.title}
                            {otherCount > 0 && ` +${otherCount} more`}
                          </p>
                        )}
                      </div>
                    );
                  })}
              </div>
            </div>
          )}

          {/* Footer with link to all docs */}
          <div className='pt-2 border-t space-y-2'>
            <Link to='/docs' className={FOOTER_LINK_CLASS} onClick={() => onOpenChange(false)}>
              View all documentation
              <ExternalLink className='h-3 w-3' />
            </Link>
            <Button onClick={handleGotIt} className='w-full' size='sm'>
              Got it!
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};
